import React, { useState } from 'react';

import { Segment, Header, Message, Loader } from 'semantic-ui-react';

import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _startCase from 'lodash/startCase';
import { connect } from 'react-redux';

import LoginRedirectOptions from 'components/modules/auth/components/LoginRedirectOptions/LoginRedirectOptions';
import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';

import BusinessSelectForm from './BusinessSelectForm';
import {
  resetBusiness as resetBusinessConnect,
  createBusiness as createBusinessConnect,
  createBusinessCore as createBusinessCoreConnect,
  initializeTimezones as initializeTimezonesConnect,
  initializeLocations as initializeLocationsConnect,
  initializeLocationTaxes as initializeLocationTaxesConnect,
  initializeMenu as initializeMenuConnect,
  initializeProviderServices as initializeProviderServicesConnect,
  initializeProviderServiceSettings as initializeProviderServiceSettingsConnect,
  initializeGalleryImages as initializeGalleryImagesConnect,
  initializeBusinessSocialMedia as initializeBusinessSocialMediaConnect,
} from '../../actions/business';
import { setContentNodes } from '../../actions/content-nodes';
import { setActiveLocation } from '../../actions/ecommerce';
import { createNotifications as createNotificationsConnect } from '../../actions/notificatons';
import {
  setProducts as setGlobalProducts,
  setActiveProduct as setGlobalActiveProduct,
} from '../../actions/products';
import {
  resetUser as resetUserConnect,
  createUser as createUserConnect,
  updateUser as updateUserConnect,
} from '../../actions/user';
import { createInputErrors as createInputErrorsConnect } from '../../actions/validation-errors';
import {
  createWebsite as createWebsiteConnect,
  initializePublications as initializePublicationsConnect,
  initializeWebsiteUsers as initializeWebsiteUsersConnect,
  initializeWebsiteSubscription as initializeWebsiteSubscriptionConnect,
  initializeDomains as initializeDomainsConnect,
  resetWebsite as resetWebsiteConnect,
  initializeLayouts as initializeLayoutsConnect,
  initializePages as initializePagesConnect,
  initializePatches as initializePatchesConnect,
  initializeStyleRules as initializeStyleRulesConnect,
  setIsNextWebsite as setIsNextWebsiteConnect,
} from '../../actions/website';
import { MERCHANT_BUSINESS_TYPE } from '../../constants/constants';
import API from '../../libs/api';
import {
  logout,
  saveTokenExpirationTime,
  isValidUser,
  isAuthorizedForSaleViews,
  signupPathFromRole,
} from '../../libs/auth';
import ErrorHandler from '../../libs/errors';
import { multiCaseGet } from '../../libs/format';
import GoogleAnalytics from '../../libs/google-analytics';
import { identifyLaunchDarklyUser } from '../../libs/launch-darkly';
import { removeObjectLikeValues } from '../../libs/objects';
import Routing from '../../libs/routing';
import { getBusinessWebsite } from '../../libs/website';
import { selectUserRoleForBusiness } from '../../selectors/business';
import WithRouter from '../modules/foundation/components/WithRouter';
import { initializeHtmlElements as initializeHtmlElementsConnect } from '../modules/html-elements/actions/html-elements';
import { setAllOrderSettings } from '../pages/ecommerce/settings/OrderSettings/OrderSettings.slice';
import { getRedirectPath } from '../routes/DashboardRoute/DashboardRoute.utils';

const firstPage = 1;

function LoginForm(props) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(['v_1', 'v_legacy']);
  const [query, setQuery] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(firstPage);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessesInitialized, setBusinessesInitialized] = useState(false);
  const businessIdQuery = (() => {
    const search = _get(props, 'location.search', '');
    const { businessId } = API.parseSearch(search);
    return businessId || '';
  })();

  const {
    user,
    resetBusiness,
    resetWebsite,
    resetUser,
    location,
    navigate,
    setIsNextWebsite,
    userRole,
    updateUser,
    createUser,
    createNotifications,
    ldClient,
    createBusiness,
    createBusinessCore,
    createInputErrors,
    createWebsite,
    initializeTimezones,
    initializeLocations,
    initializeProviderServices,
    initializeProviderServiceSettings,
    initializeBusinessSocialMedia,
    initializeLocationTaxes,
    initializeMenu,
    initializeDomains,
    initializePublications,
    initializeWebsiteUsers,
    initializeWebsiteSubscription,
    initializeLayouts,
    initializePages,
    initializeEcommerce,
    initializeOrderSettings,
    initializePatches,
    initializeStyleRules,
    initializeGalleryImages,
    initializeProducts,
    initializeActiveProduct,
    isNextWebsite,
    initializeContentNodes,
    initializeHtmlElements,
  } = props;

  // Helper to set form messages
  function setFormMessage(messageField, message, resetEventually = true) {
    if (messageField === 'errorMessage') {
      setErrorMessage(message);
      if (resetEventually) {
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  }

  function loadLocations(data) {
    initializeLocations(data);
  }

  function loadSocialMedia(mediaData) {
    const newData = mediaData.map((media) => ({ ...media, type: _startCase(media.type) }));
    initializeBusinessSocialMedia(newData);
  }

  function initializeBusiness(business) {
    createBusinessCore(business);
  }

  async function initializeBusinessAttributes(business) {
    createInputErrors();
    const { data: businessInitData } = await API.getBusinessInitializationData(
      business.type,
      business.id,
    );
    const currentWebsite = getBusinessWebsite(businessInitData, isNextWebsite);
    const coreWebsiteValues = removeObjectLikeValues(currentWebsite);
    createWebsite(coreWebsiteValues);
    const { type: businessType } = business;
    const statusContext =
      businessType === MERCHANT_BUSINESS_TYPE
        ? { business_files_created: true }
        : businessInitData.status_context || {};
    const coreBusinessValues = removeObjectLikeValues(businessInitData);
    initializeBusiness({
      ...coreBusinessValues,
      ...business,
      status_context: statusContext,
    });
    initializeTimezones({ timezones: businessInitData.timezones });
    initializeLocationTaxes(businessInitData.location_taxes);
    initializeMenu({
      schedules: businessInitData.schedules,
      categories: businessInitData.categories,
      items: businessInitData.items,
      modifier_sets: businessInitData.modifier_sets,
      modifiers: businessInitData.modifiers,
    });
    initializeDomains(businessInitData.website.domains);
    initializePublications(businessInitData.website.publications);
    initializeWebsiteUsers(currentWebsite.website_users);
    initializeWebsiteSubscription(currentWebsite.subscription_data);
    initializeLayouts(currentWebsite.layouts);
    initializePages(currentWebsite.pages);
    initializePatches(currentWebsite.patches);
    initializeStyleRules(currentWebsite.style_rules);
    initializeGalleryImages(businessInitData.gallery_images);
    initializeProviderServices(businessInitData.provider_services);
    initializeProviderServiceSettings(businessInitData.provider_service_settings);
    initializeEcommerce();
    initializeOrderSettings();
    initializeProducts(businessInitData.products);
    initializeActiveProduct(_get(businessInitData, 'products[0]'));
    initializeContentNodes(businessInitData.content_nodes);
    initializeHtmlElements(currentWebsite.html_elements);
    loadLocations(businessInitData.locations);
    loadSocialMedia(businessInitData.social_media);
  }

  async function loadBusinessIndex(index = 0) {
    const business = _get(businesses, `[${index}]`);
    if (!business) return;
    try {
      const { id, type } = business;
      createBusiness({ id, type });
      createNotifications();
      await initializeBusinessAttributes(business);
      const pathName = _get(location, 'state.from.pathname');
      if (pathName) {
        navigate(pathName);
        return;
      }
      const next = getRedirectPath();
      if (next) {
        navigate(next);
        return;
      }
      navigate(Routing.getFirstRoute(['core']));
    } catch (e) {
      ErrorHandler.capture(e);
    }
  }

  async function loadBusinesses(
    userParam = user,
    queryParam = query,
    pageParam = page,
    selectedFiltersParam = selectedFilters,
    businessIdQueryParam = businessIdQuery,
  ) {
    const finalUser = userParam || user;
    if (!isValidUser(finalUser)) {
      return;
    }
    setBusinessLoading(true);
    try {
      const result = await API.getUserBusinesses(
        queryParam || businessIdQueryParam,
        pageParam,
        'Website',
        selectedFiltersParam.reduce((filters, filterKey) => ({ ...filters, [filterKey]: 1 }), {}),
      );
      const {
        data: { total_pages: totalPagesResult, results = [] },
      } = result;
      setBusinesses(results);
      setTotalPages(totalPagesResult);
      if (!_isEmpty(results) && !finalUser.businessesCount) {
        updateUser({
          field: 'businessesCount',
          value: results.length,
          bypassTouch: true,
        });
      }
      const alwaysShowSelect = isAuthorizedForSaleViews(userRole);
      if (!alwaysShowSelect && results.length === 1 && totalPagesResult === 1 && !queryParam) {
        await loadBusinessIndex();
      }
    } catch (e) {
      ErrorHandler.capture(e);
    } finally {
      setBusinessLoading(false);
    }
  }

  const debouncedBusinessSearchChange = _debounce(async (e, target) => {
    const { value: queryValue } = target;
    setQuery(queryValue);
    setPage(firstPage);
    await loadBusinesses(undefined, queryValue, firstPage, selectedFilters, businessIdQuery);
  }, 500);
  function onBusinessSearchChange(e, target) {
    debouncedBusinessSearchChange(e, target);
  }

  async function postLogin(loginResponse) {
    const data = multiCaseGet(loginResponse, 'data') || loginResponse;

    const userData = multiCaseGet(data, 'user');
    const expirationTime =
      multiCaseGet(data, 'refreshTokenExpirationTime') || multiCaseGet(data, 'expirationTime');

    saveTokenExpirationTime(expirationTime);
    await identifyLaunchDarklyUser(userData, ldClient, { clear: true });
    GoogleAnalytics.identifyUser(userData.id);
    createUser(userData);
    createNotifications();
    await loadBusinesses(userData);
    setBusinessesInitialized(true);
  }

  async function redirectionLogin() {
    try {
      setLoading(true);
      const loginResult = await API.me();
      if (!_isEmpty(loginResult)) {
        await postLogin(loginResult);
      }
    } catch (e) {
      if (e.response && [400, 401].includes(e.response.status)) {
        setFormMessage('errorMessage', 'You are not logged in');
      } else {
        setFormMessage('errorMessage', 'Error logging in, please try again');
        ErrorHandler.capture(e, {
          extra: {
            brandingType: process.env.REACT_APP_BRANDING || 'core',
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    setLoading(true);
    await logout({ forceRedirect: true });
  }

  function signUp() {
    const signupPath = signupPathFromRole(user, userRole);
    if (signupPath) {
      navigate(signupPath);
    }
  }

  async function onBusinessFilterChange(filterKey) {
    let nextSelectedFilters = selectedFilters;
    if (selectedFilters.includes(filterKey)) {
      nextSelectedFilters = selectedFilters.filter((key) => key !== filterKey);
    } else {
      nextSelectedFilters = [...selectedFilters, filterKey];
    }
    if (nextSelectedFilters.includes('next_website')) {
      setIsNextWebsite(true);
      setPage(firstPage);
    } else {
      setIsNextWebsite(false);
    }
    setSelectedFilters(nextSelectedFilters);
    await loadBusinesses(undefined, query, firstPage, nextSelectedFilters, businessIdQuery);
  }

  function onPageChange(fulfilled, activePage) {
    setPage(activePage);
    loadBusinesses(undefined, query, activePage, selectedFilters, businessIdQuery);
  }

  function handleBusinessClick(e, target) {
    setBusinessLoading(true);
    const { index } = target;
    loadBusinessIndex(index).finally(() => setBusinessLoading(false));
  }

  function getLoginForm() {
    return (
      <div className="login-form">
        <Segment>
          <Header as="h2" textAlign="center">
            Welcome back
          </Header>

          <Header as="h3" textAlign="center">
            Choose how you&apos;d like to sign in
          </Header>

          {errorMessage && <Message error header={errorMessage} />}
          <LoginRedirectOptions hidePhoneOption />
        </Segment>
      </div>
    );
  }

  useAsyncEffect(async () => {
    setLoading(true);
    resetBusiness();
    resetWebsite();
    sessionStorage.clear();
    API.refreshBackendClient();
    if (isValidUser(user)) {
      await loadBusinesses(user);
      setBusinessesInitialized(true);
    } else {
      resetUser();
    }

    if (!isValidUser(user)) {
      try {
        await API.me();
        await redirectionLogin();
        navigate('/');
        return;
      } catch {
        // pass
      }
    }

    setLoading(false);
  }, []);

  if (loading) {
    return <Loader active />;
  }
  if (!isValidUser(user)) {
    return getLoginForm();
  }
  if (!businessesInitialized) {
    return <Loader active>Reeling in your data...Please wait!</Loader>;
  }
  return (
    <BusinessSelectForm
      onBusinessSearchChange={onBusinessSearchChange}
      onBusinessFilterChange={onBusinessFilterChange}
      handleBusinessClick={handleBusinessClick}
      onPageChange={onPageChange}
      signOut={signOut}
      signUp={signUp}
      user={user}
      businessLoading={businessLoading}
      businesses={businesses}
      totalPages={totalPages}
      page={page}
      selectedFilters={selectedFilters}
    />
  );
}

const mapDispatchToProps = (dispatch) => ({
  createUser: (payload) => dispatch(createUserConnect(payload)),
  updateUser: (payload) => dispatch(updateUserConnect(payload)),
  createWebsite: (payload) => dispatch(createWebsiteConnect(payload)),
  createBusiness: (payload) => dispatch(createBusinessConnect(payload)),
  createNotifications: (payload) => dispatch(createNotificationsConnect(payload)),
  createBusinessCore: (payload) => dispatch(createBusinessCoreConnect(payload)),
  createInputErrors: (payload) => dispatch(createInputErrorsConnect(payload)),
  initializeTimezones: (payload) => dispatch(initializeTimezonesConnect(payload)),
  initializeLocations: (payload) => dispatch(initializeLocationsConnect(payload)),
  initializeProviderServices: (payload) => dispatch(initializeProviderServicesConnect(payload)),
  initializeProviderServiceSettings: (payload) =>
    dispatch(initializeProviderServiceSettingsConnect(payload)),
  initializeBusinessSocialMedia: (payload) =>
    dispatch(initializeBusinessSocialMediaConnect(payload)),
  initializeLocationTaxes: (payload) => dispatch(initializeLocationTaxesConnect(payload)),
  initializeMenu: (payload) => dispatch(initializeMenuConnect(payload)),
  initializeDomains: (payload) => dispatch(initializeDomainsConnect(payload)),
  initializePublications: (payload) => dispatch(initializePublicationsConnect(payload)),
  initializeWebsiteUsers: (payload) => dispatch(initializeWebsiteUsersConnect(payload)),
  initializeWebsiteSubscription: (payload) =>
    dispatch(initializeWebsiteSubscriptionConnect(payload)),
  initializeLayouts: (payload) => dispatch(initializeLayoutsConnect(payload)),
  initializePages: (payload) => dispatch(initializePagesConnect(payload)),
  initializeEcommerce: () => dispatch(setActiveLocation(undefined)),
  initializeOrderSettings: () => dispatch(setAllOrderSettings([])),
  initializePatches: (payload) => dispatch(initializePatchesConnect(payload)),
  initializeStyleRules: (payload) => dispatch(initializeStyleRulesConnect(payload)),
  initializeGalleryImages: (payload) => dispatch(initializeGalleryImagesConnect(payload)),
  initializeProducts: (payload) => dispatch(setGlobalProducts(payload)),
  initializeActiveProduct: (payload) => dispatch(setGlobalActiveProduct(payload)),
  resetUser: (payload) => dispatch(resetUserConnect(payload)),
  resetBusiness: (payload) => dispatch(resetBusinessConnect(payload)),
  resetWebsite: (payload) => dispatch(resetWebsiteConnect(payload)),
  setIsNextWebsite: (payload) => dispatch(setIsNextWebsiteConnect(payload)),
  initializeContentNodes: (payload) => dispatch(setContentNodes(payload)),
  initializeHtmlElements: (payload) => dispatch(initializeHtmlElementsConnect(payload)),
});

const mapStateToProps = ({ business, website, user }) => ({
  business: _get(business, 'core.value', {}),
  userRole: selectUserRoleForBusiness({ business }),
  user: _get(user, 'core.value', {}),
  isNextWebsite: _get(website, 'isNextWebsite'),
});

export default withLDConsumer()(
  connect(mapStateToProps, mapDispatchToProps)(WithRouter(LoginForm)),
);
